import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit2, MapPin } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { getFullMediaUrl } from '../../utils/urlHelper';

import OverviewTab from './components/OverviewTab';
import TrainersTab from './components/TrainersTab';
import MembersTab from './components/MembersTab';
import AnnouncementsTab from './components/AnnouncementsTab';
import GymForm from './components/GymForm';

type TabType = 'overview' | 'members' | 'trainers' | 'announcements';

const GymDetails: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);

  const { data: gym, isLoading, error } = useQuery({
    queryKey: ['gym', gymId],
    queryFn: async () => {
      const response = await apiClient.get(`/gyms/${gymId}`);
      return response.data?.data || response.data;
    },
    enabled: !!gymId,
  });

  // Fetch trainers count
  const { data: trainersData } = useQuery({
    queryKey: ['gym-trainers', gymId],
    queryFn: async () => {
      const response = await apiClient.get(`/gyms/${gymId}/trainers`);
      return response.data?.data || response.data || { count: 0 };
    },
    enabled: !!gymId,
  });

  // Fetch members count
  const { data: membersData } = useQuery({
    queryKey: ['gym-members', gymId],
    queryFn: async () => {
      const response = await apiClient.get(`/gyms/${gymId}/members`);
      return response.data?.data || response.data || { count: 0 };
    },
    enabled: !!gymId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading gym details...</div>
      </div>
    );
  }

  if (error || !gym) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-400">Error loading gym details</div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'OVERVIEW' },
    { id: 'members' as TabType, label: 'MEMBERS' },
    { id: 'trainers' as TabType, label: 'TRAINERS' },
    { id: 'announcements' as TabType, label: 'ANNOUNCEMENTS' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button */}
        <button
          onClick={() => navigate('/gyms')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Gyms</span>
        </button>

        {/* Cover Photo */}
        <div className="mb-8 rounded-lg overflow-hidden h-64 bg-gray-900 relative">
          {gym.coverPhoto ? (
            <img
              src={gym.coverPhoto}
              alt={gym.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback if image fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : gym.photoUrl ? (
            <img
              src={getFullMediaUrl(gym.photoUrl) || ''}
              alt={gym.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-600">
              No image
            </div>
          )}

          {/* Overlay with Gym Info and Actions */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent flex flex-col justify-between p-6">
            <div></div>
            <div className="flex justify-between items-end">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">{gym.name}</h1>
                <div className="flex items-center gap-2 text-gray-300">
                  <span className="bg-[#23F47C] text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                    GYM CODE: {gym.code}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-400 mt-2">
                  <MapPin size={16} />
                  <span>{gym.address}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsEditFormOpen(true)}
                  className="flex items-center gap-2 bg-white text-gray-900 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  <Edit2 size={18} />
                  Edit Gym
                </button>
                {/* <button
                  onClick={() => {}}
                  className="flex items-center gap-2 bg-[#23F47C] text-gray-900 px-6 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-all"
                >
                  <Plus size={18} />
                  Add Staff/Member
                </button> */}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <p className="text-gray-500 text-sm uppercase mb-2">Total Members</p>
            <p className="text-white text-3xl font-bold">{membersData?.count || 0}</p>
            <p className="text-[#23F47C] text-sm mt-1">Active</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <p className="text-gray-500 text-sm uppercase mb-2">Active Trainers</p>
            <p className="text-white text-3xl font-bold">{trainersData?.count || 0}</p>
            <p className="text-[#23F47C] text-sm mt-1">Active</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-semibold text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#23F47C] border-b-2 border-[#23F47C]'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && <OverviewTab gym={gym} />}
            {activeTab === 'members' && <MembersTab gymId={gym._id} />}
            {activeTab === 'trainers' && <TrainersTab gymId={gym._id} />}
            {activeTab === 'announcements' && <AnnouncementsTab gymId={gym._id} />}
          </div>
        </div>
      </div>

      {/* Edit Form Modal */}
      {isEditFormOpen && (
        <GymForm
          gym={gym}
          onClose={() => setIsEditFormOpen(false)}
          onSuccess={() => {
            setIsEditFormOpen(false);
            queryClient.invalidateQueries({ queryKey: ['gym', gymId] });
          }}
        />
      )}
    </div>
  );
};

export default GymDetails;
